import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { EntityType } from 'app/assessment/models';
import { UserInterface } from 'app/core/user/models';
import { UserService } from 'app/core/user/services';
import { Task, TaskInterface, TaskTemplateInterface } from 'app/task/models';
import { TaskDialogDataInterface } from 'app/task/models/task-dialog-data.model';
import { TaskService } from 'app/task/services';
import { Observable, Subject } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'cz-create-new-task-dialog',
  templateUrl: './create-new-task-dialog.component.html',
  styleUrls: ['./create-new-task-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class CreateNewTaskDialogComponent implements OnInit, OnDestroy {

  public taskTemplates: TaskTemplateInterface[] = [];
  public allAssignedToOptions: UserInterface[] = [];
  public assignedToFilteredOptions: Observable<UserInterface[]>;
  public allSubjectOptions: UserInterface[] = [];
  public subjectFilteredOptions: Observable<UserInterface[]>;
  public task: TaskInterface;
  public showSurveyTaskError: boolean = false;

  public taskCreateFormGroup: FormGroup;
  public taskTypeControl: FormControl;
  public subjectControl: FormControl;
  public assignedToControl: FormControl;
  public dueDateControl: FormControl;
  public hoursControl: FormControl;
  public notesControl: FormControl;
  public summaryControl: FormControl;
  public visibleToForUserControl: FormControl;

  private readonly _destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private readonly _userService: UserService,
              private readonly _taskService: TaskService,
              @Inject(MAT_DIALOG_DATA) public data: TaskDialogDataInterface,
              private readonly _dialogRef: MatDialogRef<CreateNewTaskDialogComponent>) {

    this.data = this.data ? this.data : {};

    // Initialize the task
    this.task = new Task({
      assignedTo: this.data.assignedTo,
      subject: this.data.subject,
      dueDate: this.data.dueDate ? this.data.dueDate : new Date(),
    });

    this.initializeReactiveForm();

    // Load relevant data.
    this._taskService.getTaskTemplates()
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe((taskTemplates: TaskTemplateInterface[]) => {
        this.taskTemplates = taskTemplates;
      });

    // Nest these so we only call the API endpoint once.
    this._userService.loadAll()
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe(((users: UserInterface[]): void => {
        this.allSubjectOptions = users;
        this.subjectFilteredOptions = this.buildUserArrayFilter(this.subjectControl, this.allSubjectOptions);

        // Do not call the API, just return from state.
        this._userService.findAllUsersForAssignedToList(false)
        .pipe(
          take(1),
        )
        .subscribe((assignedToUsers: UserInterface[]) => {
          this.allAssignedToOptions = assignedToUsers;
          this.assignedToFilteredOptions = this.buildUserArrayFilter(this.assignedToControl, this.allAssignedToOptions);
        });
      }).bind(this));
  }

  public ngOnInit(): void {

    // Update the default values when the user changes the task template.
    this.taskTypeControl.valueChanges.pipe(
      takeUntil(this._destroy$),
    ).subscribe((taskTemplate: TaskTemplateInterface) => {
      this.task.hours = taskTemplate.defaultHours;
      this.task.dueDate = taskTemplate.defaultDueDate;
      this.task.summary = taskTemplate.defaultSummary;
      this.task.notes = taskTemplate.defaultNotes;
      this.showSurveyTaskError = false;

      this.taskCreateFormGroup.patchValue({
        hoursCtrl: this.task.hours,
        dueDateCtrl: this.task.dueDate,
        summaryCtrl: this.task.summary,
        notesCtrl: this.task.notes,
      });
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  public createTask(): void {
    if (!this.taskCreateFormGroup.valid) {
      return;
    }

    let formAssignedTo: UserInterface = this.assignedToControl.value;
    let formSubject: UserInterface = this.subjectControl.value;

    if(!formAssignedTo.id) {
      const filteredList: UserInterface[] = this.filterUserOptions(this.assignedToControl.value, this.allAssignedToOptions);
      if(filteredList.length === 1) {
        formAssignedTo = filteredList[0];
      } else {
        this.assignedToControl.setValue('');
        return;
      }
    }

    if(!formSubject.id) {
      const filteredList: UserInterface[] = this.filterUserOptions(this.subjectControl.value, this.allSubjectOptions);
      if(filteredList.length === 1) {
        formSubject = filteredList[0];
      } else {
        this.subjectControl.setValue('');
        return;
      }
    }

    const newTask: TaskInterface = {
      ...this.task,
      assignedTo: formAssignedTo,
      subject: formSubject,
      dueDate: this.dueDateControl.value,
      hours: this.hoursControl.value,
      notes: this.notesControl.value,
      summary: this.summaryControl.value,
      isVisibleToSubject: this.visibleToForUserControl.value,
    };

    const selectedTemplate: TaskTemplateInterface = this.taskTypeControl.value as TaskTemplateInterface;
    newTask.taskTemplateName = selectedTemplate.templateName;
    newTask.taskType = selectedTemplate.id;
    newTask.taskTypeLabel = selectedTemplate.taskName;

    if (newTask.taskTemplateName === EntityType.SURVEY && newTask.assignedTo.id !== newTask.subject.id) {
      this.showSurveyTaskError = true;
    } else {
      this._taskService.create(newTask)
        .pipe(takeUntil(this._destroy$))
        .subscribe((newTask: TaskInterface) => {
          if(!newTask || newTask.id < 1) { return; }
          this._dialogRef.close(newTask);
        });
    }
  }

  public autocompleteUserOption(user?: UserInterface): string | undefined {
    return user ? `${user.firstName} ${user.lastName}` : undefined;
  }

  private buildUserArrayFilter(formControl: AbstractControl, userArray: UserInterface[]): Observable<UserInterface[]> {
    return formControl.valueChanges
    .pipe(
      startWith<string | UserInterface>(''),
      map((value: string | UserInterface) => {
        if (!value) { return ''; }

        return typeof value === 'string' ? value : `${value.firstName} ${value.lastName}`;
      }),
      map((name: string) => name ? this.filterUserOptions(name, userArray) : [...userArray]),
    );
  }

  private filterUserOptions(name: string, users:  UserInterface[]): UserInterface[] {
    const filterValue: string = name.toLowerCase();

    return users.filter((option: UserInterface) => {
      const fullName: string = `${option.firstName} ${option.lastName}`;
      return fullName.toLowerCase().includes(filterValue);
    });
  }

  private initializeReactiveForm(): void {
    // Initialize form
    this.taskTypeControl = new FormControl({ value: '' }, Validators.required);
    this.assignedToControl = new FormControl({ value: '' }, Validators.required);
    this.subjectControl = new FormControl({ value: '' }, Validators.required);
    this.dueDateControl = new FormControl({ value: '' }, Validators.required);
    this.hoursControl = new FormControl({ value: '' }, Validators.required);
    this.notesControl = new FormControl({ value: '' });
    this.summaryControl = new FormControl({ value: '' });
    this.visibleToForUserControl = new FormControl({ value: undefined });

    this.taskCreateFormGroup = new FormGroup({
      taskTypeCtrl: this.taskTypeControl,
      assignedToCtrl: this.assignedToControl,
      subjectCtrl: this.subjectControl,
      dueDateCtrl: this.dueDateControl,
      hoursCtrl: this.hoursControl,
      notesCtrl: this.notesControl,
      summaryCtrl: this.summaryControl,
      visibleToForCtrl: this.visibleToForUserControl,
    });

    // Set defaults.
    this.taskCreateFormGroup.patchValue({
      taskTypeCtrl: '',
      assignedToCtrl: this.task.assignedTo,
      subjectCtrl: this.task.subject,
      dueDateCtrl: this.task.dueDate,
      hoursCtrl: 0,
      notesCtrl: '',
      summaryCtrl: '',
      visibleToForCtrl: true,
    });
  }
}
